/**
 * Mobile Error Boundary Component
 * Catches errors and provides user-friendly error UI with retry
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ThemedButton } from './ui/ThemedButton';
import MobileCard from './MobileCard';
import { useMobile } from '../contexts/MobileContext';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onRetry={this.handleRetry} mobile={this.props.mobile} />;
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, onRetry, mobile }) => {
  return (
    <div className={`${mobile?.isMobile ? 'p-4' : 'p-6'} min-h-screen flex items-center justify-center`}>
      <MobileCard variant="danger" className="max-w-md w-full">
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="text-6xl mb-4"
          >
            ⚠️
          </motion.div>
          
          <h2 className="text-xl font-semibold text-red-400 mb-2">
            Something went wrong
          </h2>
          
          <p className="text-gray-400 text-sm mb-6">
            Something interrupted the dashboard. Tap reload and you should be back in.
          </p>
          
          <div className="flex flex-col space-y-2">
            <ThemedButton
              variant="primary"
              onClick={onRetry}
              size="md"
              className="w-full"
            >
              Try Again
            </ThemedButton>
            
            <ThemedButton
              variant="secondary"
              onClick={() => window.location.reload()}
              size="md"
              className="w-full"
            >
              Reload Page
            </ThemedButton>
          </div>
          
          {process.env.NODE_ENV === 'development' && error && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-gray-500 cursor-pointer">Error Details</summary>
              <pre className="text-xs text-gray-600 mt-2 overflow-auto">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </MobileCard>
    </div>
  );
};

const MobileErrorBoundary = ({ children, onRetry }) => {
  const mobile = useMobile();
  
  return (
    <ErrorBoundaryClass onRetry={onRetry} mobile={mobile}>
      {children}
    </ErrorBoundaryClass>
  );
};

export default MobileErrorBoundary;







